import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Leaf, TrendingUp, Calendar, MapPin, DollarSign, Recycle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface PortfolioHolding {
  id: string;
  projectName: string;
  projectType: string;
  location: string;
  vintageYear: number;
  credits: number;
  purchasePrice: number;
  currentValue: number;
  acquisitionDate: string;
  registry: string;
}

const mockHoldings: PortfolioHolding[] = [
  {
    id: '1',
    projectName: 'Amazon Rainforest Conservation',
    projectType: 'Forest Conservation',
    location: 'Brazil',
    vintageYear: 2023,
    credits: 500,
    purchasePrice: 15000,
    currentValue: 17500,
    acquisitionDate: '2024-01-15',
    registry: 'VCS',
  },
  {
    id: '2',
    projectName: 'Solar Farm Development',
    projectType: 'Renewable Energy',
    location: 'California, USA',
    vintageYear: 2024,
    credits: 1000,
    purchasePrice: 32000,
    currentValue: 35000,
    acquisitionDate: '2024-02-10',
    registry: 'Gold Standard',
  },
  {
    id: '3',
    projectName: 'Mangrove Restoration',
    projectType: 'Blue Carbon',
    location: 'Indonesia',
    vintageYear: 2023,
    credits: 300,
    purchasePrice: 12000,
    currentValue: 13500,
    acquisitionDate: '2024-03-05',
    registry: 'VCS',
  },
];

export const PortfolioTab = () => {
  const [showRetireModal, setShowRetireModal] = useState(false);
  const [selectedHolding, setSelectedHolding] = useState<PortfolioHolding | null>(null);
  const [showSellModal, setShowSellModal] = useState(false);

  const totalCredits = mockHoldings.reduce((sum, holding) => sum + holding.credits, 0);
  const totalCurrentValue = mockHoldings.reduce((sum, holding) => sum + holding.currentValue, 0);
  const totalPurchaseValue = mockHoldings.reduce((sum, holding) => sum + holding.purchasePrice, 0);
  const totalGainLoss = totalCurrentValue - totalPurchaseValue;
  const gainLossPercentage = ((totalGainLoss / totalPurchaseValue) * 100).toFixed(1);

  const handleRetire = (holding: PortfolioHolding) => {
    setSelectedHolding(holding);
    setShowRetireModal(true);
  };

  const handleSell = (holding: PortfolioHolding) => {
    setSelectedHolding(holding);
    setShowSellModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Portfolio Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Credits</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalCredits.toLocaleString()}</div>
            <p className="text-sm text-muted-foreground">VCC Holdings</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Portfolio Value</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${totalCurrentValue.toLocaleString()}</div>
            <p className="text-sm text-muted-foreground">Current market value</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Gain/Loss</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${totalGainLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {totalGainLoss >= 0 ? '+' : ''}${totalGainLoss.toLocaleString()}
            </div>
            <p className={`text-sm ${totalGainLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {gainLossPercentage}% from purchase
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Holdings List */}
      <Card>
        <CardHeader>
          <CardTitle>Your Holdings</CardTitle>
          <CardDescription>
            Manage your carbon credit portfolio and retirement activities
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {mockHoldings.map((holding) => {
            const gainLoss = holding.currentValue - holding.purchasePrice;
            const gainLossPercent = ((gainLoss / holding.purchasePrice) * 100).toFixed(1);
            
            return (
              <div key={holding.id} className="border rounded-lg p-4 space-y-4">
                <div className="flex justify-between items-start">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{holding.projectName}</h3>
                      <Badge variant="outline">{holding.registry}</Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Leaf className="w-4 h-4" />
                        {holding.projectType}
                      </div>
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {holding.location}
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Vintage {holding.vintageYear}
                      </div>
                    </div>
                  </div>
                  <div className="text-right space-y-1">
                    <div className="text-lg font-bold">{holding.credits} credits</div>
                    <div className="text-sm text-muted-foreground">
                      ${(holding.currentValue / holding.credits).toFixed(2)} per credit
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <div className="text-sm font-medium">Purchase Value</div>
                    <div className="text-lg">${holding.purchasePrice.toLocaleString()}</div>
                    <div className="text-xs text-muted-foreground">
                      Acquired {new Date(holding.acquisitionDate).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-sm font-medium">Current Value</div>
                    <div className="text-lg">${holding.currentValue.toLocaleString()}</div>
                    <div className={`text-xs ${gainLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {gainLoss >= 0 ? '+' : ''}${gainLoss.toLocaleString()} ({gainLossPercent}%)
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRetire(holding)}
                      className="flex items-center gap-1"
                    >
                      <Recycle className="w-4 h-4" />
                      Retire
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSell(holding)}
                      className="flex items-center gap-1"
                    >
                      <DollarSign className="w-4 h-4" />
                      List for Sale
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Retirement Feature - Coming Soon */}
      {showRetireModal && selectedHolding && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Retire Credits</CardTitle>
              <CardDescription>
                Retire your {selectedHolding.projectName} credits
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center py-8">
                <p className="text-muted-foreground">
                  Credit retirement feature coming soon...
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRetireModal(false);
                    setSelectedHolding(null);
                  }}
                  className="mt-4"
                >
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sell Credits Modal - Placeholder for now */}
      {showSellModal && selectedHolding && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>List Credits for Sale</CardTitle>
              <CardDescription>
                List your {selectedHolding.projectName} credits on the marketplace
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center py-8">
                <p className="text-muted-foreground">
                  Sell credits feature coming soon...
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowSellModal(false);
                    setSelectedHolding(null);
                  }}
                  className="mt-4"
                >
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};